import React, { ReactNode, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Badge from "antd/es/badge";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import Located from "@mapmycustomers/shared/types/base/Located";
import { Company, Deal, Person } from "@mapmycustomers/shared/types/entity";
import { isNotEmptyString } from "@mapmycustomers/shared/util/assert";
import anyColorToHex from "@mapmycustomers/shared/util/color/anyColorToHex";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";
import { Avatar } from "@mapmycustomers/ui";

import FieldGrid from "@app/component/FieldGrid";
import Checkbox from "@app/component/input/Checkbox";
import RadioButton from "@app/component/input/RadioButton";
import RecordPreviewConfigurationColumns from "@app/store/recordPreview/RecordPreviewConfigurationColumns";
import { getRecordPreviewConfigurationColumns } from "@app/store/recordPreview/selectors";
import { RootState } from "@app/store/rootReducer";
import { TOPMOST_MODAL_ZINDEX } from "@app/util/consts";
import locatedToAddress from "@app/util/locatedToAddress";
import loggingService from "@app/util/logging";
import getMapEntityColor from "@app/util/map/getMapEntityColor";

import styles from "./AssociationRow.module.scss";

interface Props {
  columns: RecordPreviewConfigurationColumns;
  disabled?: boolean;
  entity: Company | Deal | Person;
  multiselect?: boolean;
  onChange?: (entityId: Identified["id"], value: boolean) => void;
  selected: boolean;
  tooltipMessage?: ReactNode;
}

const AssociationRow = ({
  columns,
  disabled,
  entity,
  multiselect,
  onChange,
  selected = false,
  tooltipMessage,
}: Props) => {
  const intl = useIntl();

  const entityColumns = useMemo(() => {
    return (columns?.[entity.entity] ?? []).filter(
      (field) =>
        !field.hasFeature(FieldFeature.MAP_PINNED_FIELD) &&
        field.isReadable &&
        field.hasNonEmptyValueFor(entity)
    );
  }, [columns, entity]);

  const labelId = `association_${entity.entity}:${entity.id}`;

  const style = useMemo(
    () => ({
      borderColor: anyColorToHex(getMapEntityColor(entity) ?? Color.BLACK),
    }),
    [entity]
  );

  const handleChange = useCallback(
    (checked: boolean, event: CheckboxChangeEvent) => {
      event.stopPropagation();
      if (disabled) {
        return;
      }
      onChange?.(entity.id, checked);
    },
    [disabled, entity, onChange]
  );

  const handleClick = useCallback(() => {
    if (disabled) {
      return;
    }
    onChange?.(entity.id, !selected);
  }, [disabled, entity, onChange, selected]);

  const info = useMemo(() => {
    if ([EntityType.COMPANY, EntityType.PERSON].includes(entity.entity)) {
      const companyOrPerson = entity as Company | Person;
      return [
        getFormattedAddressForUi(locatedToAddress(companyOrPerson as Located)),
        ...(entity.entity === EntityType.PERSON
          ? [entity.primaryAccount?.name ?? entity.accounts[0]?.name ?? ""]
          : []),
        companyOrPerson.phone,
        companyOrPerson.email,
      ]
        .filter(isNotEmptyString)
        .join(" • ");
    } else if (entity.entity === EntityType.DEAL) {
      const deal = entity as Deal;
      return (
        <>
          <Badge className={styles.badge} color={deal.stage.color} text={deal.stage.name} />
          {` • ${[deal.funnel.name, deal.account?.name, deal.contact?.name]
            .filter(isNotEmptyString)
            .join(" • ")}`}
        </>
      );
    } else {
      loggingService.error(`Unexpected entity type in AssociationRow's entity: ${entity.entity}`, {
        entity,
      });
      return null;
    }
  }, [entity]);

  return (
    <div
      className={cn(styles.item, { [styles.disabled]: disabled })}
      onClick={handleClick}
      style={style}
    >
      {multiselect !== undefined && (
        <div className={styles.control}>
          <Tooltip title={tooltipMessage} trigger={disabled && tooltipMessage ? ["hover"] : []}>
            <span className={styles.tooltipTarget}>
              {multiselect ? (
                <Checkbox
                  checked={selected}
                  disabled={disabled}
                  id={labelId}
                  onChange={handleChange}
                />
              ) : (
                <RadioButton
                  checked={selected}
                  disabled={disabled}
                  id={labelId}
                  onChange={handleChange}
                />
              )}
            </span>
          </Tooltip>
        </div>
      )}

      <div className={cn(styles.details, { [styles.disabled]: disabled })}>
        <div className={styles.name}>
          <span>{entity.name}</span>

          {entityColumns.length > 0 && (
            <span className={styles.info}>
              <Popover
                arrowPointAtCenter
                content={
                  <div className={styles.content}>
                    <div className={styles.title}>
                      {intl.formatMessage({
                        id: "associations.pinnedFields.title",
                        defaultMessage: "Pinned Fields",
                        description: "Pinned Fields popover title",
                      })}
                    </div>

                    <div className={styles.body}>
                      <FieldGrid
                        className={styles.grid}
                        columns={entityColumns}
                        entity={entity}
                        showEmpty={false}
                      />
                    </div>
                  </div>
                }
                destroyTooltipOnHide
                mouseLeaveDelay={0}
                placement="rightTop"
                // modal has TOPMOST_MODAL_ZINDEX, so we need to override popover's z-index to make it appear over the modal
                zIndex={TOPMOST_MODAL_ZINDEX}
              >
                <FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} />
              </Popover>
            </span>
          )}
        </div>
        <span className={styles.infoText}>
          <Avatar
            className={styles.avatar}
            key={`${entity.id}_${entity.user.id}`}
            size="xs"
            user={entity.user}
          />
          <span className={styles.metadata}>{info}</span>
        </span>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  columns: getRecordPreviewConfigurationColumns(state),
});

export default connect(mapStateToProps)(AssociationRow);
